/**
 * IndexedDB Search Storage
 *
 * Local-first storage implementation for search indexes and documents.
 * All search data is stored in IndexedDB with no network calls for privacy.
 */

import type {
  SearchDocument,
  SearchIndexData,
  SearchQueueItem,
  SearchHistoryItem,
  SearchFilter,
  SearchSettings,
  SearchStats,
} from '../types';
import {
  SEARCH_DB_NAME,
  SEARCH_DB_VERSION,
  SEARCH_STORES,
  DEFAULT_SEARCH_SETTINGS,
} from '../types';
import * as pako from 'pako';

export class IndexedDBSearchStorage {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  constructor() {
    this.initPromise = this.initialize();
  }

  /**
   * Initialize IndexedDB database
   */
  private async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(SEARCH_DB_NAME, SEARCH_DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open search database:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create search index store
        if (!db.objectStoreNames.contains(SEARCH_STORES.INDEX)) {
          db.createObjectStore(SEARCH_STORES.INDEX, {
            keyPath: 'id',
          });
        }

        // Create documents store
        if (!db.objectStoreNames.contains(SEARCH_STORES.DOCUMENTS)) {
          const docStore = db.createObjectStore(SEARCH_STORES.DOCUMENTS, {
            keyPath: 'id',
          });
          // Create indexes for efficient queries
          docStore.createIndex('type', 'type', { unique: false });
          docStore.createIndex('lastModified', 'lastModified', {
            unique: false,
          });
          docStore.createIndex('lastIndexed', 'lastIndexed', {
            unique: false,
          });
          // Compound index for efficient update checks
          docStore.createIndex('id_lastIndexed', ['id', 'lastIndexed'], {
            unique: false,
          });
        }

        // Create queue store
        if (!db.objectStoreNames.contains(SEARCH_STORES.QUEUE)) {
          const queueStore = db.createObjectStore(SEARCH_STORES.QUEUE, {
            keyPath: 'id',
          });
          queueStore.createIndex(
            'priority_created',
            ['priority', 'createdAt'],
            {
              unique: false,
            }
          );
          queueStore.createIndex('fileId', 'fileId', { unique: false });
        }

        // Create history store
        if (!db.objectStoreNames.contains(SEARCH_STORES.HISTORY)) {
          const historyStore = db.createObjectStore(SEARCH_STORES.HISTORY, {
            keyPath: 'id',
          });
          historyStore.createIndex('timestamp', 'timestamp', {
            unique: false,
          });
        }

        // Create filters store
        if (!db.objectStoreNames.contains(SEARCH_STORES.FILTERS)) {
          const filtersStore = db.createObjectStore(SEARCH_STORES.FILTERS, {
            keyPath: 'id',
          });
          filtersStore.createIndex('isPreset', 'isPreset', { unique: false });
        }

        // Create settings store
        if (!db.objectStoreNames.contains(SEARCH_STORES.SETTINGS)) {
          db.createObjectStore(SEARCH_STORES.SETTINGS, {
            keyPath: 'id',
          });
        }
      };
    });
  }

  /**
   * Ensure database is initialized
   */
  private async ensureInitialized(): Promise<void> {
    if (this.initPromise) {
      await this.initPromise;
    }
    if (!this.db) {
      throw new Error('Search database not initialized');
    }
  }

  /**
   * Save search index
   */
  async saveIndex(indexData: SearchIndexData): Promise<void> {
    await this.ensureInitialized();

    // Compress index data if enabled
    if (indexData.isCompressed) {
      const compressed = pako.deflate(indexData.indexData);
      indexData.indexData = btoa(String.fromCharCode(...compressed));
      indexData.sizeBytes = compressed.length;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.INDEX],
        'readwrite'
      );
      const store = transaction.objectStore(SEARCH_STORES.INDEX);

      const request = store.put(indexData);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Load search index
   */
  async loadIndex(): Promise<SearchIndexData | null> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.INDEX],
        'readonly'
      );
      const store = transaction.objectStore(SEARCH_STORES.INDEX);

      const request = store.get('main');

      request.onsuccess = () => {
        const indexData = request.result;
        if (indexData && indexData.isCompressed) {
          // Decompress index data
          const compressed = Uint8Array.from(atob(indexData.indexData), (c) =>
            c.charCodeAt(0)
          );
          const decompressed = pako.inflate(compressed, { to: 'string' });
          indexData.indexData = decompressed;
        }
        resolve(indexData || null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save a search document
   */
  async saveDocument(document: SearchDocument): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.DOCUMENTS],
        'readwrite'
      );
      const store = transaction.objectStore(SEARCH_STORES.DOCUMENTS);

      const request = store.put(document);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save multiple documents
   */
  async saveDocuments(documents: SearchDocument[]): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.DOCUMENTS],
        'readwrite'
      );
      const store = transaction.objectStore(SEARCH_STORES.DOCUMENTS);

      let savedCount = 0;
      const totalDocs = documents.length;

      if (totalDocs === 0) {
        resolve();
        return;
      }

      documents.forEach((doc) => {
        const request = store.put(doc);

        request.onsuccess = () => {
          savedCount++;
          if (savedCount === totalDocs) {
            resolve();
          }
        };

        request.onerror = () => reject(request.error);
      });
    });
  }

  /**
   * Get a document by ID
   */
  async getDocument(id: string): Promise<SearchDocument | null> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.DOCUMENTS],
        'readonly'
      );
      const store = transaction.objectStore(SEARCH_STORES.DOCUMENTS);

      const request = store.get(id);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all documents
   */
  async getAllDocuments(): Promise<SearchDocument[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.DOCUMENTS],
        'readonly'
      );
      const store = transaction.objectStore(SEARCH_STORES.DOCUMENTS);

      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get documents that need reindexing
   */
  async getDocumentsToReindex(): Promise<SearchDocument[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.DOCUMENTS],
        'readonly'
      );
      const store = transaction.objectStore(SEARCH_STORES.DOCUMENTS);

      const request = store.getAll();

      request.onsuccess = () => {
        const documents = request.result || [];
        // Filter documents where lastModified > lastIndexed
        const toReindex = documents.filter((doc) => {
          if (!doc.lastIndexed) return true;
          return doc.lastModified > doc.lastIndexed;
        });
        resolve(toReindex);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Remove a document
   */
  async removeDocument(id: string): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.DOCUMENTS],
        'readwrite'
      );
      const store = transaction.objectStore(SEARCH_STORES.DOCUMENTS);

      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Add item to search queue
   */
  async addToQueue(item: SearchQueueItem): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.QUEUE],
        'readwrite'
      );
      const store = transaction.objectStore(SEARCH_STORES.QUEUE);

      const request = store.put(item);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get next queue item
   */
  async getNextQueueItem(): Promise<SearchQueueItem | null> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.QUEUE],
        'readonly'
      );
      const store = transaction.objectStore(SEARCH_STORES.QUEUE);
      const index = store.index('priority_created');

      // Get highest priority, oldest item
      const request = index.openCursor(null, 'prev');

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          resolve(cursor.value);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Remove item from queue
   */
  async removeFromQueue(id: string): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.QUEUE],
        'readwrite'
      );
      const store = transaction.objectStore(SEARCH_STORES.QUEUE);

      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all queue items
   */
  async clearQueue(): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.QUEUE],
        'readwrite'
      );
      const store = transaction.objectStore(SEARCH_STORES.QUEUE);

      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Add search to history
   */
  async addToHistory(item: SearchHistoryItem): Promise<void> {
    await this.ensureInitialized();

    const settings = await this.getSettings();
    if (!settings.enableHistory) return;

    // Enforce history limit
    const history = await this.getHistory();
    if (history.length >= settings.maxHistoryItems) {
      // Remove oldest items
      const toRemove = history
        .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
        .slice(0, history.length - settings.maxHistoryItems + 1);

      for (const item of toRemove) {
        await this.removeFromHistory(item.id);
      }
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.HISTORY],
        'readwrite'
      );
      const store = transaction.objectStore(SEARCH_STORES.HISTORY);

      const request = store.put(item);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get search history
   */
  async getHistory(limit?: number): Promise<SearchHistoryItem[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.HISTORY],
        'readonly'
      );
      const store = transaction.objectStore(SEARCH_STORES.HISTORY);
      const index = store.index('timestamp');

      // Get newest first
      const request = index.openCursor(null, 'prev');
      const results: SearchHistoryItem[] = [];

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor && (!limit || results.length < limit)) {
          results.push(cursor.value);
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Remove item from history
   */
  async removeFromHistory(id: string): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.HISTORY],
        'readwrite'
      );
      const store = transaction.objectStore(SEARCH_STORES.HISTORY);

      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear search history
   */
  async clearHistory(): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.HISTORY],
        'readwrite'
      );
      const store = transaction.objectStore(SEARCH_STORES.HISTORY);

      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save search filter
   */
  async saveFilter(filter: SearchFilter): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.FILTERS],
        'readwrite'
      );
      const store = transaction.objectStore(SEARCH_STORES.FILTERS);

      const request = store.put(filter);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all filters
   */
  async getFilters(): Promise<SearchFilter[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.FILTERS],
        'readonly'
      );
      const store = transaction.objectStore(SEARCH_STORES.FILTERS);

      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Remove filter
   */
  async removeFilter(id: string): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.FILTERS],
        'readwrite'
      );
      const store = transaction.objectStore(SEARCH_STORES.FILTERS);

      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get search settings
   */
  async getSettings(): Promise<SearchSettings> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.SETTINGS],
        'readonly'
      );
      const store = transaction.objectStore(SEARCH_STORES.SETTINGS);

      const request = store.get('default');

      request.onsuccess = () => {
        resolve(request.result || DEFAULT_SEARCH_SETTINGS);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Update search settings
   */
  async updateSettings(settings: Partial<SearchSettings>): Promise<void> {
    await this.ensureInitialized();

    const currentSettings = await this.getSettings();
    const newSettings = { ...currentSettings, ...settings, id: 'default' };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [SEARCH_STORES.SETTINGS],
        'readwrite'
      );
      const store = transaction.objectStore(SEARCH_STORES.SETTINGS);

      const request = store.put(newSettings);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get search statistics
   */
  async getStatistics(): Promise<SearchStats> {
    await this.ensureInitialized();

    const [documents, indexData, history] = await Promise.all([
      this.getAllDocuments(),
      this.loadIndex(),
      this.getHistory(100),
    ]);

    // Calculate statistics
    const topTerms = new Map<string, number>();
    history.forEach((item) => {
      const terms = item.query.query.toLowerCase().split(/\s+/);
      terms.forEach((term) => {
        topTerms.set(term, (topTerms.get(term) || 0) + 1);
      });
    });

    const topSearchTerms = Array.from(topTerms.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([term, count]) => ({ term, count }));

    const successfulQueries = history.filter((h) => h.successful);
    const averageQueryTime =
      successfulQueries.length > 0
        ? successfulQueries.reduce((sum, h) => sum + (h.resultCount || 0), 0) /
          successfulQueries.length
        : 0;

    return {
      totalDocuments: documents.length,
      totalIndexSize: indexData?.sizeBytes || 0,
      indexVersion: indexData?.version || 0,
      lastRebuild: indexData?.updatedAt,
      averageQueryTime,
      cacheHitRate: 0, // Will be calculated by cache manager
      topSearchTerms,
    };
  }

  /**
   * Clear all search data
   */
  async clearAll(): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [
          SEARCH_STORES.INDEX,
          SEARCH_STORES.DOCUMENTS,
          SEARCH_STORES.QUEUE,
          SEARCH_STORES.HISTORY,
          SEARCH_STORES.FILTERS,
        ],
        'readwrite'
      );

      const stores = [
        transaction.objectStore(SEARCH_STORES.INDEX),
        transaction.objectStore(SEARCH_STORES.DOCUMENTS),
        transaction.objectStore(SEARCH_STORES.QUEUE),
        transaction.objectStore(SEARCH_STORES.HISTORY),
        transaction.objectStore(SEARCH_STORES.FILTERS),
      ];

      let clearedCount = 0;
      const totalStores = stores.length;

      stores.forEach((store) => {
        const request = store.clear();

        request.onsuccess = () => {
          clearedCount++;
          if (clearedCount === totalStores) {
            resolve();
          }
        };

        request.onerror = () => reject(request.error);
      });
    });
  }

  /**
   * Close database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.initPromise = null;
    }
  }
}
